import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import {
  useGetAppealsQuery,
  useTraderApproveAppealMutation,
  useTraderRejectAppealMutation,
} from "../store/trader-api";
import { useNavigate } from "react-router-dom";

const AppealsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 0.75rem;
    gap: 1rem;
  }
`;

const FiltersCard = styled.div`
  background: var(--bg-card);
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const Select = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-input);
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: var(--bg-card);

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: var(--focus-ring);
  }
`;

const Input = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-input);
  border-radius: 0.375rem;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: var(--focus-ring);
  }
`;

const AppealsCard = styled.div`
  background: var(--bg-card);
  border-radius: 0.75rem;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  @media (max-width: 768px) {
    display: none;
  }
`;

const TableHeader = styled.thead`
  background: var(--bg-primary);

  @media (max-width: 768px) {
    display: none;
  }
`;

/* ====== Mobile Appeal Cards ====== */
const MobileAppealsList = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 0.75rem;
  }
`;

const MobileAppealCard = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--border-divider);
  border-radius: 0.75rem;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s;

  &:active {
    background: var(--bg-primary);
    transform: scale(0.99);
  }
`;

const MobileAppealHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const MobileAppealId = styled.span`
  font-family: 'SF Mono', 'Monaco', monospace;
  font-size: 0.8125rem;
  color: var(--accent-primary);
  font-weight: 600;
`;

const MobileAppealBody = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem 1rem;
`;

const MobileAppealField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

const MobileAppealLabel = styled.span`
  font-size: 0.6875rem;
  color: var(--text-muted);
  font-weight: 500;
  text-transform: uppercase;
`;

const MobileAppealValue = styled.span`
  font-size: 0.8125rem;
  color: var(--text-primary);
  font-weight: 600;
`;

const MobileAppealFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--border-divider);
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid var(--border-divider);
  cursor: pointer;

  &:hover {
    background: var(--bg-primary);
  }
`;

const TableHeaderCell = styled.th`
  padding: 1rem;
  text-align: left;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const TableCell = styled.td`
  padding: 1rem;
  font-size: 0.875rem;
  color: var(--text-primary);
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  background: ${(props) => {
    switch (props.status?.toUpperCase()) {
      case "PENDING":
        return "var(--status-pending-bg)";
      case "IN_PROGRESS":
        return "var(--alert-info-bg)";
      case "APPROVED":
        return "var(--status-success-bg)";
      case "REJECTED":
        return "var(--status-error-bg)";
      case "SUPPORT_REVIEW":
        return "var(--alert-info-bg)";
      case "TRADER_REVIEW":
        return "var(--status-pending-bg)";
      case "MERCHANT_RESP_PENDING":
        return "var(--alert-warning-bg)";
      default:
        return "var(--bg-secondary)";
    }
  }};
  color: ${(props) => {
    switch (props.status?.toUpperCase()) {
      case "PENDING":
        return "var(--status-pending-text)";
      case "IN_PROGRESS":
        return "var(--alert-info-text)";
      case "APPROVED":
        return "var(--status-success-text)";
      case "REJECTED":
        return "var(--status-error-text)";
      case "SUPPORT_REVIEW":
        return "var(--alert-info-text)";
      case "TRADER_REVIEW":
        return "var(--status-pending-text)";
      case "MERCHANT_RESP_PENDING":
        return "var(--alert-warning-text)";
      default:
        return "var(--text-primary)";
    }
  }};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--text-secondary);
`;

const Header = styled.div`
  margin-bottom: 1.5rem;
`;

const Subtitle = styled.p`
  margin: 0.5rem 0 0 0;
  color: var(--text-muted);
  font-size: 1rem;
`;

// Action buttons
const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button<{ variant: "approve" | "reject" }>`
  padding: 0.375rem 0.75rem;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  background: ${(props) =>
    props.variant === "approve" ? "var(--accent-success)" : "var(--accent-error)"};
  color: #ffffff;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

// Modal styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bg-modal-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--bg-card);
  border-radius: 0.75rem;
  padding: 1.5rem;
  width: 100%;
  max-width: 500px;
  box-shadow: var(--shadow-lg);
  max-height: 90vh;
  overflow-y: auto;

  @media (max-width: 768px) {
    width: 95%;
    padding: 1.25rem;
    max-height: 85vh;
  }
`;

const ModalTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const ModalDescription = styled.p`
  margin: 0 0 1.5rem 0;
  font-size: 0.875rem;
  color: var(--text-muted);
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-input);
  border-radius: 0.375rem;
  font-size: 0.875rem;
  min-height: 100px;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: var(--focus-ring);
  }
`;

const FileUploadArea = styled.div<{ hasFiles: boolean }>`
  border: 2px dashed ${(props) => (props.hasFiles ? "var(--accent-success)" : "var(--border-input)")};
  border-radius: 0.5rem;
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: ${(props) => (props.hasFiles ? "var(--alert-success-bg)" : "transparent")};

  &:hover {
    border-color: var(--accent-primary);
    background: var(--bg-secondary);
  }
`;

const FileList = styled.div`
  margin-top: 0.75rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  background: var(--bg-secondary);
  border-radius: 0.25rem;
  font-size: 0.75rem;
`;

const RemoveFileButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: var(--accent-error);
  padding: 0;
  font-size: 0.875rem;
  line-height: 1;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
`;

const ModalButton = styled.button<{ variant: "primary" | "secondary" }>`
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  background: ${(props) =>
    props.variant === "primary" ? "var(--accent-error)" : "transparent"};
  color: ${(props) => (props.variant === "primary" ? "#ffffff" : "var(--text-primary)")};
  border: 1px solid ${(props) =>
    props.variant === "primary" ? "var(--accent-error)" : "var(--border-input)"};

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: var(--accent-error);
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

// Компонент обратного таймера
const CountdownTimer = ({ deadline }: { deadline: string }) => {
  const [timeLeft, setTimeLeft] = useState('');
  const [isExpired, setIsExpired] = useState(false);
  
  useEffect(() => {
    const updateTimer = () => {
      const now = new Date().getTime();
      const end = new Date(deadline).getTime();
      const diff = end - now;
      
      if (diff <= 0) {
        setTimeLeft('Истекло');
        setIsExpired(true);
        return;
      }
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      if (hours > 0) {
        setTimeLeft(`${hours}ч ${minutes}м ${seconds}с`);
      } else if (minutes > 0) {
        setTimeLeft(`${minutes}м ${seconds}с`);
      } else {
        setTimeLeft(`${seconds}с`);
      }
      setIsExpired(false);
    };
    
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    
    return () => clearInterval(interval);
  }, [deadline]);
  
  return (
    <span style={{
      color: isExpired ? 'var(--accent-error)' : 'var(--accent-warning)',
      fontWeight: 600,
      fontSize: '0.875rem'
    }}>
      {timeLeft}
    </span>
  );
};

function Appeals() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState("all");
  const [orderFilter, setOrderFilter] = useState("");
  const [page] = useState(1);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAppealId, setSelectedAppealId] = useState<string | null>(null);
  const [rejectComment, setRejectComment] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<{ comment?: string; files?: string }>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  // API hooks
  const {
    data: appealsResponse,
    isLoading,
    error,
    refetch,
  } = useGetAppealsQuery({
    page,
    limit: 20,
    status: statusFilter === "all" ? undefined : statusFilter,
    orderId: orderFilter || undefined,
  }, {
    pollingInterval: 10000, // Автообновление каждые 10 секунд
    refetchOnMountOrArgChange: true,
  });

  const [traderApprove, { isLoading: isApproving }] = useTraderApproveAppealMutation();
  const [traderReject, { isLoading: isRejecting }] = useTraderRejectAppealMutation();

  const appeals = appealsResponse?.data || [];
  const totalAppeals = appealsResponse?.meta?.total || 0;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getStatusText = (status: string) => {
    switch (status?.toUpperCase()) {
      case "PENDING":
        return "На рассмотрении";
      case "IN_PROGRESS":
        return "В обработке";
      case "APPROVED":
        return "Одобрена";
      case "REJECTED":
        return "Отклонена";
      case "SUPPORT_REVIEW":
        return "На рассмотрении саппорта";
      case "TRADER_REVIEW":
        return "На повторном рассмотрении";
      case "MERCHANT_RESP_PENDING":
        return "На рассмотрении мерчанта";
      default:
        return status;
    }
  };

  const handleAppealClick = (appealId: string) => {
    navigate(`/appeals/${appealId}`);
  };

  const handleApprove = async (e: React.MouseEvent, appealId: string) => {
    e.stopPropagation();
    if (window.confirm("Вы уверены, что хотите одобрить апелляцию? Это означает, что платеж был получен.")) {
      try {
        await traderApprove(appealId).unwrap();
        refetch();
      } catch (err) {
        console.error("Ошибка одобрения апелляции:", err);
        alert("Не удалось одобрить апелляцию");
      }
    }
  };

  const handleOpenRejectModal = (e: React.MouseEvent, appealId: string) => {
    e.stopPropagation();
    setSelectedAppealId(appealId);
    setRejectComment("");
    setFiles([]);
    setErrors({});
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAppealId(null);
    setRejectComment("");
    setFiles([]);
    setErrors({});
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFiles((prev) => [...prev, ...newFiles]);
      setErrors((prev) => ({ ...prev, files: undefined }));
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleReject = async () => {
    const newErrors: { comment?: string; files?: string } = {};

    if (!rejectComment.trim()) {
      newErrors.comment = "Необходимо указать причину отклонения";
    }

    if (files.length === 0) {
      newErrors.files = "Необходимо приложить доказательство";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (!selectedAppealId) return;

    try {
      const formData = new FormData();
      formData.append("comment", rejectComment);
      files.forEach((file) => {
        formData.append("files", file);
      });

      await traderReject({ appealId: selectedAppealId, formData }).unwrap();
      handleCloseModal();
      refetch();
    } catch (err) {
      console.error("Ошибка отклонения апелляции:", err);
      alert("Не удалось отклонить апелляцию");
    }
  };

  if (isLoading) {
    return (
      <AppealsContainer>
        <div style={{ textAlign: "center", padding: "2rem" }}>
          Загрузка апелляций...
        </div>
      </AppealsContainer>
    );
  }

  if (error) {
    return (
      <AppealsContainer>
        <div style={{ textAlign: "center", padding: "2rem", color: "var(--accent-error)" }}>
          Ошибка загрузки апелляций
        </div>
      </AppealsContainer>
    );
  }

  return (
    <>
      <AppealsContainer>
        <Header>
          <Subtitle>
            Управление апелляциями по ордерам. Всего: {totalAppeals}
          </Subtitle>
        </Header>

        <FiltersCard>
          <FiltersGrid>
            <FilterGroup>
              <Label>Статус</Label>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">Все статусы</option>
                <option value="PENDING">На рассмотрении</option>
                <option value="APPROVED">Одобрена</option>
                <option value="REJECTED">Отклонена</option>
                <option value="SUPPORT_REVIEW">На рассмотрении саппорта</option>
              </Select>
            </FilterGroup>

            <FilterGroup>
              <Label>ID Ордера</Label>
              <Input
                type="text"
                placeholder="Поиск по ID ордера"
                value={orderFilter}
                onChange={(e) => setOrderFilter(e.target.value)}
              />
            </FilterGroup>
          </FiltersGrid>
        </FiltersCard>

        <AppealsCard>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>ID Апелляции</TableHeaderCell>
                <TableHeaderCell>Создано</TableHeaderCell>
                <TableHeaderCell>Срок</TableHeaderCell>
                <TableHeaderCell>Номер транзакции</TableHeaderCell>
                <TableHeaderCell>Реквизит</TableHeaderCell>
                <TableHeaderCell>Сумма (RUB)</TableHeaderCell>
                <TableHeaderCell>Причина</TableHeaderCell>
                <TableHeaderCell>Статус</TableHeaderCell>
                <TableHeaderCell>Действие</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <tbody>
              {appeals.length === 0 ? (
                <tr>
                  <TableCell
                    colSpan={8}
                    style={{ textAlign: "center", padding: "2rem" }}
                  >
                    <EmptyState>Нет апелляций</EmptyState>
                  </TableCell>
                </tr>
              ) : (
                appeals.map((appeal: any) => (
                  <TableRow
                    key={appeal.id}
                    onClick={() => handleAppealClick(appeal.id)}
                  >
                    <TableCell>#{appeal.id.substring(0, 8)}</TableCell>
                    <TableCell>{formatDate(appeal.createdAt)}</TableCell>
                    <TableCell>
                      {appeal.deadline ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Решить до:</span>
                          <span style={{ fontSize: '0.8rem' }}>{formatDate(appeal.deadline)}</span>
                          {(appeal.status === 'PENDING' || appeal.status === 'SUPPORT_REVIEW' || appeal.status === 'TRADER_REVIEW') && (
                            <CountdownTimer deadline={appeal.deadline} />
                          )}
                        </div>
                      ) : '—'}
                    </TableCell>
                    <TableCell>{appeal.order?.invId || appeal.orderId}</TableCell>
                    <TableCell>
                      {appeal.order?.requisite?.cardNumber ||
                        appeal.order?.requisite?.phoneNumber ||
                        appeal.order?.requisite?.sbpPhoneNumber ||
                        appeal.order?.requisite?.accountNumber ||
                        appeal.order?.cardNumber ||
                        appeal.order?.phoneNumber ||
                        appeal.order?.accountNumber ||
                        "—"}
                    </TableCell>
                    <TableCell>
                      {appeal.order?.amount ? `${appeal.order.amount} ₽` : "—"}
                    </TableCell>
                    <TableCell>{appeal.reason}</TableCell>
                    <TableCell>
                      <StatusBadge status={appeal.status}>
                        {getStatusText(appeal.status)}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      {(appeal.status?.toUpperCase() === "PENDING" || appeal.status?.toUpperCase() === "TRADER_REVIEW") && (
                        <ActionButtonsContainer>
                          <ActionButton
                            variant="approve"
                            onClick={(e) => handleApprove(e, appeal.id)}
                            disabled={isApproving}
                          >
                            ✓ Одобрить
                          </ActionButton>
                          <ActionButton
                            variant="reject"
                            onClick={(e) => handleOpenRejectModal(e, appeal.id)}
                            disabled={isRejecting}
                          >
                            ✗ Отклонить
                          </ActionButton>
                        </ActionButtonsContainer>
                      )}
                      {appeal.status?.toUpperCase() === "SUPPORT_REVIEW" && (
                        <span style={{ color: "var(--text-secondary)", fontSize: "0.75rem" }}>
                          Ожидает решения саппорта
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>

          {/* Mobile Appeal Cards */}
          <MobileAppealsList>
            {appeals.length === 0 ? (
              <EmptyState>Нет апелляций</EmptyState>
            ) : (
              appeals.map((appeal: any) => (
                <MobileAppealCard key={appeal.id} onClick={() => handleAppealClick(appeal.id)}>
                  <MobileAppealHeader>
                    <MobileAppealId>#{appeal.id.substring(0, 8)}</MobileAppealId>
                    <StatusBadge status={appeal.status}>
                      {getStatusText(appeal.status)}
                    </StatusBadge>
                  </MobileAppealHeader>
                  <MobileAppealBody>
                    <MobileAppealField>
                      <MobileAppealLabel>Транзакция</MobileAppealLabel>
                      <MobileAppealValue>{appeal.order?.invId || appeal.orderId}</MobileAppealValue>
                    </MobileAppealField>
                    <MobileAppealField>
                      <MobileAppealLabel>Сумма</MobileAppealLabel>
                      <MobileAppealValue>
                        {appeal.order?.amount ? `${appeal.order.amount} ₽` : "—"}
                      </MobileAppealValue>
                    </MobileAppealField>
                    <MobileAppealField style={{ gridColumn: '1 / -1' }}>
                      <MobileAppealLabel>Причина</MobileAppealLabel>
                      <MobileAppealValue style={{ fontSize: '0.75rem', fontWeight: 500 }}>
                        {appeal.reason}
                      </MobileAppealValue>
                    </MobileAppealField>
                  </MobileAppealBody>
                  <MobileAppealFooter>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      {formatDate(appeal.createdAt)}
                    </span>
                    {(appeal.status?.toUpperCase() === "PENDING" || appeal.status?.toUpperCase() === "TRADER_REVIEW") && (
                      <ActionButtonsContainer>
                        <ActionButton
                          variant="approve"
                          onClick={(e) => handleApprove(e, appeal.id)}
                          disabled={isApproving}
                        >
                          ✓
                        </ActionButton>
                        <ActionButton
                          variant="reject"
                          onClick={(e) => handleOpenRejectModal(e, appeal.id)}
                          disabled={isRejecting}
                        >
                          ✗
                        </ActionButton>
                      </ActionButtonsContainer>
                    )}
                  </MobileAppealFooter>
                </MobileAppealCard>
              ))
            )}
          </MobileAppealsList>
        </AppealsCard>
      </AppealsContainer>

      {/* Модальное окно для отклонения */}
      {isModalOpen && (
        <ModalOverlay onClick={handleCloseModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Отклонение апелляции</ModalTitle>
            <ModalDescription>
              Для отклонения апелляции необходимо приложить доказательство и указать причину. 
              После отклонения апелляция будет передана на рассмотрение саппорта.
            </ModalDescription>

            <FormGroup>
              <FormLabel>Доказательство (обязательно)</FormLabel>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                multiple
                accept="image/*,video/*,.pdf"
                style={{ display: "none" }}
              />
              <FileUploadArea
                hasFiles={files.length > 0}
                onClick={() => fileInputRef.current?.click()}
              >
                {files.length === 0 ? (
                  <>
                    <div>📎 Нажмите для загрузки файлов</div>
                    <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "0.5rem" }}>
                      Изображения, видео или PDF
                    </div>
                  </>
                ) : (
                  <div style={{ color: "var(--accent-success)" }}>
                    ✓ Загружено {files.length} файл(ов)
                  </div>
                )}
              </FileUploadArea>
              {files.length > 0 && (
                <FileList>
                  {files.map((file, index) => (
                    <FileItem key={index}>
                      {file.name}
                      <RemoveFileButton onClick={() => removeFile(index)}>
                        ×
                      </RemoveFileButton>
                    </FileItem>
                  ))}
                </FileList>
              )}
              {errors.files && <ErrorMessage>{errors.files}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <FormLabel>Причина отклонения (обязательно)</FormLabel>
              <TextArea
                value={rejectComment}
                onChange={(e) => {
                  setRejectComment(e.target.value);
                  setErrors((prev) => ({ ...prev, comment: undefined }));
                }}
                placeholder="Опишите причину отклонения апелляции..."
              />
              {errors.comment && <ErrorMessage>{errors.comment}</ErrorMessage>}
            </FormGroup>

            <ModalButtons>
              <ModalButton variant="secondary" onClick={handleCloseModal}>
                Отмена
              </ModalButton>
              <ModalButton
                variant="primary"
                onClick={handleReject}
                disabled={isRejecting}
              >
                {isRejecting ? "Отправка..." : "Отклонить апелляцию"}
              </ModalButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
}

export default Appeals;
